import React, { useMemo } from 'react';
import type { TextFieldProps } from '@mui/material';
import { Box, Chip, CircularProgress, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import { Search } from '@mui/icons-material';
import { NetworkRequestState } from 'c-data-layer';
import { TextField } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import type { SystemSearchEntityLoading } from '../../types';
import systemSearchColours from '../../Lib/systemSearchColours';

const useStyles = makeStyles((theme: Theme) => ({
    searchInput: {
        caretColor: systemSearchColours(theme).inputText,
        color: systemSearchColours(theme).inputText,
        fontSize: '24px',
        borderBottom: 'black',
    },
}));

const SystemSearchSearchField = React.forwardRef(function SystemSearchSearchField(
    {
        entityResults,
        resultCount,
        ...props
    }: TextFieldProps & {
        entityResults: SystemSearchEntityLoading;
        // page and entity results combined, as listed below the field
        resultCount: number;
    },
    ref,
) {
    const errorSearching = useCommonTranslation('SystemSearch.errorSearching');
    const noResults = useCommonTranslation('SystemSearch.noResults');
    const { searchInput } = useStyles();
    const endAdornment = useMemo(() => {
        if (entityResults.state === NetworkRequestState.InProgress) {
            return (
                <InputAdornment position="end">
                    <Box id="system-search-spinner" mr="10px" color="white">
                        <CircularProgress size="30px" color="inherit" />
                    </Box>
                </InputAdornment>
            );
        }
        if (entityResults.state === NetworkRequestState.Error) {
            return (
                <InputAdornment position="end">
                    <Chip size="small" label={errorSearching} color="secondary" />
                </InputAdornment>
            );
        }
        if (entityResults.state === NetworkRequestState.Success && resultCount === 0) {
            return (
                <InputAdornment position="end">
                    <Chip size="small" label={noResults} />
                </InputAdornment>
            );
        }
        return null;
    }, [entityResults.state, resultCount, errorSearching, noResults]);
    return (
        <TextField
            {...props}
            whiteBackdrop={false}
            greyBackdrop={false}
            variant="standard"
            placeholder={useCommonTranslation('SystemSearch.defaultSearchPlaceholder')}
            spellCheck={false}
            InputProps={{
                className: searchInput,
                startAdornment: (
                    <InputAdornment position="start">
                        <Search color="inherit" />
                    </InputAdornment>
                ),
                endAdornment,
            }}
            autoFocus
            ref={ref}
        />
    );
});

export default SystemSearchSearchField;
